import React, { useState, useEffect, useCallback } from 'react';
import { getLeads, getLeadStats } from '../api/leadsApi';
import { getPipeline } from '../api/pipelineApi';
import { useAuth } from '../context/AuthContext';
import LeadForm from '../components/LeadForm';
import LeadDetail from '../components/LeadDetail';
import UpworkBadge from '../components/UpworkBadge';

// Legacy hardcoded stages — used only while no custom pipeline is configured.
// Also imported by LeadsPage for stage pills.
export const STAGES = [
  { key: 'prospect', label: 'Prospect', color: '#888780', bg: '#F1EFE8' },
  { key: 'contacted', label: 'Contacted', color: '#185FA5', bg: '#E6F1FB' },
  { key: 'qualified', label: 'Qualified', color: '#3B6D11', bg: '#EAF3DE' },
  { key: 'discovery_scheduled', label: 'Discovery Call', color: '#BA7517', bg: '#FAEEDA' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: '#534AB7', bg: '#EEEDFE' },
  { key: 'negotiation', label: 'Negotiation', color: '#993C1D', bg: '#FAECE7' },
  { key: 'closed_won', label: 'Won ✓', color: '#0F6E56', bg: '#E1F5EE' },
  { key: 'closed_lost', label: 'Lost', color: '#A32D2D', bg: '#FCEBEB' },
  { key: 'retainer', label: '⭐ Retainer', color: '#854F0B', bg: '#FAEEDA' },
  { key: 're_nurture', label: 'Re-nurture', color: '#533AB7', bg: '#EEEDFE' },
];

// Colors for configured-pipeline columns, keyed by builder stage key.
const STAGE_KEY_COLORS = {
  lead: { color: '#888780', bg: '#F1EFE8' },
  contacted: { color: '#185FA5', bg: '#E6F1FB' },
  qualified: { color: '#3B6D11', bg: '#EAF3DE' },
  discovery_call: { color: '#BA7517', bg: '#FAEEDA' },
  proposal_sent: { color: '#534AB7', bg: '#EEEDFE' },
  negotiation: { color: '#993C1D', bg: '#FAECE7' },
  won: { color: '#0F6E56', bg: '#E1F5EE' },
  lost: { color: '#A32D2D', bg: '#FCEBEB' },
  retainer: { color: '#854F0B', bg: '#FAEEDA' },
  re_nurture: { color: '#533AB7', bg: '#EEEDFE' },
};
const DEFAULT_STAGE_COLORS = { color: '#534AB7', bg: '#EEEDFE' };

const SOURCE_LABELS = { upwork: 'Upwork', client_referral: 'Client Ref.', personal_referral: 'Personal Ref.', linkedin: 'LinkedIn', inbound: 'Inbound', other: 'Other' };
const SOURCE_COLORS = { upwork: '#14a800', client_referral: '#534AB7', personal_referral: '#0F6E56', linkedin: '#0077b5', inbound: '#BA7517', other: '#888' };

// Builds Kanban columns from a configured pipeline: one column per placed node,
// in the builder's left-bar order, duplicate stage labels disambiguated.
function columnsFromPipeline(pipeline) {
  const seen = {};
  return pipeline.nodes.map((n) => {
    seen[n.stageKey] = (seen[n.stageKey] || 0) + 1;
    const colors = STAGE_KEY_COLORS[n.stageKey] || DEFAULT_STAGE_COLORS;
    return {
      key: n.nodeId,
      label: `${n.icon ? n.icon + ' ' : ''}${n.label}${seen[n.stageKey] > 1 ? ` · ${seen[n.stageKey]}` : ''}`,
      color: colors.color,
      bg: colors.bg,
    };
  });
}

export default function PipelinePage({ onOpenBuilder }) {
  const { isSuperAdmin } = useAuth();
  const [pipeline, setPipeline] = useState(null); // configured pipeline doc, or null
  const [pipelineLoaded, setPipelineLoaded] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFollowUps, setShowFollowUps] = useState(false);
  const [followUpLeads, setFollowUpLeads] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);

  const configured = !!pipeline;

  const fetchPipeline = useCallback(async () => {
    try {
      const r = await getPipeline();
      setPipeline(r.data.configured ? r.data.pipeline : null);
    } catch (e) { setPipeline(null); }
    finally { setPipelineLoaded(true); }
  }, []);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      // Legacy stage filtering happens server-side; configured-pipeline
      // filtering is client-side by node id (the server filter only knows
      // the legacy stage enum).
      if (filterStage && !configured) params.stage = filterStage;
      if (search) params.search = search;
      const [leadsRes, statsRes] = await Promise.all([getLeads(params), getLeadStats()]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStage, search, configured]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const openFollowUps = async () => {
    setShowFollowUps(true);
    setFollowUpsLoading(true);
    try {
      const res = await getLeads({ followUpDue: 'true' });
      setFollowUpLeads(res.data);
    } catch (e) { console.error(e); }
    finally { setFollowUpsLoading(false); }
  };

  // ── Columns + grouping ─────────────────────────────────────────────────────

  const UNDEFINED_KEY = '__undefined__';
  const entryNodeId = configured ? (pipeline.nodes.find(n => n.stageKey === 'lead') || {}).nodeId : null;

  // A lead only counts as "undefined" if it was actually assigned to a real
  // node that has since been removed — a lead that was never assigned at all
  // (pipelineNodeId unset) still defaults to the entry stage, same as before.
  const isUndefined = (lead) =>
    configured && !!lead.pipelineNodeId && !pipeline.nodes.some(n => n.nodeId === lead.pipelineNodeId);

  const columnKeyForLead = (lead) => {
    if (!configured) return lead.stage;
    if (isUndefined(lead)) return UNDEFINED_KEY;
    return lead.pipelineNodeId || entryNodeId;
  };

  const undefinedLeadCount = configured ? leads.filter(isUndefined).length : 0;
  const showUndefinedColumn = configured && pipeline.showUndefinedStageCard !== false && undefinedLeadCount > 0;

  const columns = configured ? (() => {
    const base = columnsFromPipeline(pipeline);
    if (!showUndefinedColumn) return base;
    const undefinedCol = { key: UNDEFINED_KEY, label: 'Undefined Stages', color: '#999', bg: '#ECEAE4', italic: true };
    return pipeline.undefinedStageCardPosition === 'last' ? [...base, undefinedCol] : [undefinedCol, ...base];
  })() : STAGES;

  const visibleLeads = configured && filterStage
    ? leads.filter(l => columnKeyForLead(l) === filterStage)
    : leads;

  const groupedLeads = columns.reduce((acc, col) => {
    acc[col.key] = visibleLeads.filter(l => columnKeyForLead(l) === col.key);
    return acc;
  }, {});

  const stageDisplayForLead = (lead) => {
    if (configured) {
      if (isUndefined(lead)) {
        return { label: lead.pipelineNodeLabel ? `${lead.pipelineNodeIcon || ''} ${lead.pipelineNodeLabel}`.trim() : 'Undefined stage', color: '#999' };
      }
      const node = pipeline.nodes.find(n => n.nodeId === columnKeyForLead(lead));
      const colors = node ? (STAGE_KEY_COLORS[node.stageKey] || DEFAULT_STAGE_COLORS) : DEFAULT_STAGE_COLORS;
      return { label: node ? node.label : '—', color: colors.color };
    }
    const st = STAGES.find(x => x.key === lead.stage);
    return { label: st?.label || lead.stage, color: st?.color || '#888' };
  };

  return (
    <div style={s.page}>
      {/* Pipeline configuration banner (super admins) */}
      {isSuperAdmin() && pipelineLoaded && (
        configured ? (
          <div style={s.pipelineBanner}>
            <span>Board is running on your custom pipeline: <strong>{pipeline.name}</strong></span>
            <button style={s.pipelineBannerBtn} onClick={onOpenBuilder}>Edit pipeline</button>
          </div>
        ) : (
          <div style={{ ...s.pipelineBanner, background: '#FAEEDA', borderColor: '#FAC775' }}>
            <span style={{ color: '#854F0B' }}>No custom pipeline configured yet — build one to define your own stages and flow.</span>
            <button style={s.pipelineBannerBtn} onClick={onOpenBuilder}>Configure pipeline</button>
          </div>
        )
      )}

      {/* Stats bar */}
      {stats && (
        <div style={s.statsBar}>
          <div style={s.stat}><span style={s.statNum}>{stats.totalLeads}</span><span style={s.statLabel}>Total leads</span></div>
          <div style={{ ...s.stat, cursor: 'pointer' }} onClick={openFollowUps} title="Click to view follow-up leads">
            <span style={{ ...s.statNum, color: '#BA7517' }}>{stats.followUpToday}</span>
            <span style={{ ...s.statLabel, color: '#BA7517', textDecoration: 'underline dotted' }}>Follow-ups today</span>
          </div>
          <div style={s.stat}><span style={{ ...s.statNum, color: '#534AB7' }}>{stats.reNurtureCount}</span><span style={s.statLabel}>Re-nurture</span></div>
          <div style={{ ...s.stat, marginLeft: 'auto' }}>
            <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Add lead</button>
          </div>
        </div>
      )}

      {/* Search + filter */}
      <div style={s.toolbar}>
        <input style={s.search} placeholder="Search by name, company, email..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
          <option value="">All stages</option>
          {columns.map(col => <option key={col.key} value={col.key}>{col.label}</option>)}
        </select>
      </div>

      {/* Kanban board */}
      <div style={s.board}>
        {columns.map(col => (
          <div key={col.key} style={s.col}>
            <div style={s.colHeader}>
              <span style={{ ...s.colDot, background: col.color }} />
              <span style={{ ...s.colTitle, ...(col.italic ? s.colTitleItalic : {}) }}>{col.label}</span>
              <span style={{ ...s.colCount, background: col.bg, color: col.color }}>{groupedLeads[col.key]?.length || 0}</span>
            </div>
            <div style={s.colBody}>
              {loading ? <div style={s.empty}>Loading...</div> :
               groupedLeads[col.key]?.length === 0 ? <div style={s.empty}>No leads</div> :
               groupedLeads[col.key]?.map(lead => (
                <div key={lead._id} style={s.card} onClick={() => setSelectedLead(lead)}>
                  <div style={s.cardName}>
                    {lead.source === 'upwork' && <UpworkBadge size={13} style={{ marginRight: 5 }} />}
                    {lead.name}
                  </div>
                  <div style={s.cardCompany}>{lead.company}</div>
                  {col.key === UNDEFINED_KEY && (
                    <div style={s.undefinedStageTag}>Last stage: {stageDisplayForLead(lead).label}</div>
                  )}
                  <div style={s.cardMeta}>
                    <span style={{ ...s.sourceTag, background: SOURCE_COLORS[lead.source] + '18', color: SOURCE_COLORS[lead.source] }}>
                      {SOURCE_LABELS[lead.source] || lead.source}
                    </span>
                    {lead.dealSize && <span style={s.dealTag}>{lead.dealSize}</span>}
                  </div>
                  {lead.nextFollowUpDate && new Date(lead.nextFollowUpDate) <= new Date() && (
                    <div style={s.followUpAlert}>⏰ Follow-up due</div>
                  )}
                  {lead.assignedToName && <div style={s.assignee}>👤 {lead.assignedToName}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {showForm && <LeadForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchLeads(); }} />}
      {selectedLead && <LeadDetail leadId={selectedLead._id} onClose={() => { setSelectedLead(null); fetchLeads(); }} />}

      {/* Follow-ups today modal */}
      {showFollowUps && (
        <div style={s.fuOverlay} onClick={e => e.target === e.currentTarget && setShowFollowUps(false)}>
          <div style={s.fuModal}>
            <div style={s.fuHeader}>
              <div>
                <div style={s.fuTitle}>Follow-ups due today</div>
                <div style={s.fuSub}>{followUpsLoading ? 'Loading...' : `${followUpLeads.length} lead${followUpLeads.length !== 1 ? 's' : ''}`}</div>
              </div>
              <button style={s.close} onClick={() => setShowFollowUps(false)}>×</button>
            </div>
            <div style={s.fuBody}>
              {followUpsLoading ? (
                <div style={s.fuEmpty}>Loading...</div>
              ) : followUpLeads.length === 0 ? (
                <div style={s.fuEmpty}>No follow-ups due today.</div>
              ) : followUpLeads.map(lead => {
                const display = stageDisplayForLead(lead);
                return (
                  <div key={lead._id} style={s.fuCard} onClick={() => { setShowFollowUps(false); setSelectedLead(lead); }}>
                    <div style={s.fuCardRow}>
                      <div>
                        <div style={s.fuName}>{lead.name}</div>
                        <div style={s.fuCompany}>{lead.company}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={s.fuDate}>⏰ {new Date(lead.nextFollowUpDate).toLocaleDateString()}</div>
                        <div style={{ ...s.fuStage, color: display.color }}>{display.label}</div>
                      </div>
                    </div>
                    {lead.nextFollowUpNote && <div style={s.fuNote}>{lead.nextFollowUpNote}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '1rem', fontFamily: 'Inter, system-ui, sans-serif', minHeight: '100vh', background: '#f5f4f0' },
  pipelineBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#EEEDFE', border: '1px solid #d6d3f5', borderRadius: 12, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#534AB7', fontWeight: 500 },
  pipelineBannerBtn: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 500, cursor: 'pointer', flexShrink: 0 },
  statsBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, background: '#fff', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  stat: { display: 'flex', flexDirection: 'column', minWidth: 80 },
  statNum: { fontSize: 22, fontWeight: 600, lineHeight: 1.1, color: '#1a1a1a' },
  statLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  addBtn: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  toolbar: { display: 'flex', gap: 8, marginBottom: 16 },
  search: { flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, background: '#fff', outline: 'none' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, background: '#fff', outline: 'none', minWidth: 140 },
  board: { display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start' },
  col: { minWidth: 220, maxWidth: 220, background: '#eeede9', borderRadius: 12, padding: '10px 8px', flexShrink: 0 },
  colHeader: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, padding: '0 4px' },
  colDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  colTitle: { fontSize: 12, fontWeight: 600, color: '#444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colTitleItalic: { fontStyle: 'italic', color: '#888' },
  colCount: { fontSize: 11, padding: '1px 7px', borderRadius: 10, fontWeight: 600 },
  colBody: { minHeight: 60 },
  empty: { fontSize: 12, color: '#aaa', textAlign: 'center', padding: '1rem 0' },
  card: { background: '#fff', borderRadius: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', transition: 'transform .1s', border: '1px solid #f0efe9' },
  cardName: { fontSize: 13, fontWeight: 600, color: '#1a1a1a', marginBottom: 2 },
  cardCompany: { fontSize: 12, color: '#666', marginBottom: 6 },
  cardMeta: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  sourceTag: { fontSize: 10, padding: '2px 7px', borderRadius: 8, fontWeight: 500 },
  dealTag: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#f0efe9', color: '#666' },
  followUpAlert: { fontSize: 11, color: '#BA7517', marginTop: 6, fontWeight: 500 },
  assignee: { fontSize: 11, color: '#888', marginTop: 4 },
  undefinedStageTag: { fontSize: 11, fontStyle: 'italic', color: '#999', marginBottom: 6 },
  fuOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  fuModal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  fuHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f0efe9' },
  fuTitle: { fontSize: 16, fontWeight: 600, color: '#1a1a1a' },
  fuSub: { fontSize: 12, color: '#888', marginTop: 2 },
  fuBody: { padding: '12px 16px', overflowY: 'auto', flex: 1 },
  fuEmpty: { fontSize: 13, color: '#aaa', textAlign: 'center', padding: '2rem 0' },
  fuCard: { padding: '12px', borderRadius: 10, border: '1px solid #f0efe9', marginBottom: 8, cursor: 'pointer', background: '#fafafa' },
  fuCardRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  fuName: { fontSize: 14, fontWeight: 600, color: '#1a1a1a' },
  fuCompany: { fontSize: 12, color: '#666', marginTop: 2 },
  fuDate: { fontSize: 12, fontWeight: 600, color: '#BA7517' },
  fuStage: { fontSize: 11, fontWeight: 500, marginTop: 2 },
  fuNote: { fontSize: 12, color: '#555', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0efe9' },
  close: { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', lineHeight: 1, padding: 0 },
};
